<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': expandAll }"
                        v-b-tooltip.hover.bottom :title="expandAll ? 'Contraer grupos' : 'Desplegar todos los grupos'"
                        @click="toggleExpandAll()">
                <AppIcon :name="expandAll ? 'unfold_less' : 'unfold_more'" />
              </b-button>
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button variant="link" class="list-view-icon-btn" v-b-tooltip.hover.bottom
                        title="Refrescar" aria-label="Refrescar" @click="filter()">
                <AppIcon name="refresh" />
              </b-button>
              <b-button variant="link" class="list-view-icon-btn list-view-icon-btn--primary" v-b-tooltip.hover.bottom
                        title="Agregar servidor" aria-label="Agregar servidor" @click="add()">
                <AppIcon name="add" />
              </b-button>
            </div>
          </div>
        </section>
        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 220px; max-width: 320px;">
              <label class="filter-label">Buscar</label>
              <b-form-input type="text" size="sm" placeholder="Nombre o subdominio" v-model="filters.name" v-on:keyup.enter.native="filter" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 200px; max-width: 280px;">
              <label class="filter-label">Agrupación</label>
              <v-select :options="arr.select.categories" v-model="filters.categorie" placeholder="Todas" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 200px; max-width: 280px;">
              <label class="filter-label">Cliente</label>
              <v-select :options="arr.select.customers" v-model="filters.customer" placeholder="Todos" :multiple="false" :select-on-tab="true" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 200px; max-width: 280px;">
              <label class="filter-label">Proveedor</label>
              <v-select :options="arr.select.suppliers" v-model="filters.supplier" placeholder="Todos" :multiple="false" :select-on-tab="true" />
            </div>
            <b-button variant="outline-dark" @click="filter()" size="sm" v-b-tooltip.hover title="Aplicar filtros"
                      style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <MonitorSummary class="mb-3" :tiles="summaryTiles" :active="quickFilter" @select="quickFilter = $event" />

      <b-card>
        <div class="instances-monitor-toolbar">
          <MonitorGroupBy v-model="groupBy" :options="groupOptions" />
        </div>

        <b-alert v-if="idsFilter.length" show variant="warning" class="d-flex align-items-center justify-content-between py-2">
          <span><AppIcon name="notifications" class="mr-1" /> Mostrando {{idsFilter.length}} servidores sin reportes de Monitor (desde una notificación).</span>
          <b-button size="sm" variant="outline-dark" @click="clearIdsFilter()"><AppIcon name="close" class="mr-1" /> Ver todos</b-button>
        </b-alert>

        <div v-if="table.isBusy" class="text-center py-4" role="status" aria-live="polite">
          <b-spinner variant="primary" label="Cargando servidores"></b-spinner>
        </div>

        <div v-else-if="visibleItems.length">
          <MonitorTree :nodes="tree" :items="visibleItems" :expand-all="expandAll" :force-expand="idsFilter.length > 0" :summarize="getGroupHealth" :reveal-id="revealId" storage-key="tree_open_crud_instances_monitor_server" @manual="expandAll = false">
            <template #health="{ summary }">
                <span v-if="summary.avisos" class="text-warning" v-b-tooltip.hover :title="summary.avisos + ' servidor(es) con avisos de Monitor'">
                  <AppIcon name="warning" /> {{summary.avisos}}
                </span>
                <span v-if="summary.attention" class="text-muted ml-2" v-b-tooltip.hover :title="summary.attention + ' servidor(es) sin reportes o con reporte atrasado'">
                  <AppIcon name="schedule" /> {{summary.attention}}
                </span>
                <span v-if="summary.pending" class="text-info ml-2" v-b-tooltip.hover :title="summary.pending + ' servidor(es) pendientes de emparejar en Monitor'">
                  <AppIcon name="link_off" /> {{summary.pending}}
                </span>
            </template>
            <template #leaf="{ items }">
              <b-table class="app-table mb-0 instances-monitor-table"
                       responsive
                       :stacked="isMobileViewport ? 'md' : false"
                       :hover="true"
                       :small="true"
                       :items="items"
                       :fields="tableFields"
                       :tbody-tr-class="rowClass"
                       @row-clicked="openDrawer">

                <template v-slot:cell(name)="data">
                  <div class="instances-monitor__identity">
                    <span class="instances-monitor__avatar">
                      <AppIcon name="dns" />
                    </span>
                    <span class="instances-monitor__identity-text">
                      <b>{{data.item.name}}</b>
                      <small class="text-muted">{{data.item.subdomain || 'Sin subdominio'}}</small>
                    </span>
                  </div>
                </template>

                <template v-slot:cell(customer)="data">
                  <MonitorAvatarName :entity="data.item.customer" empty-label="Sin cliente" />
                </template>

                <template v-slot:cell(supplier)="data">
                  <MonitorAvatarName :entity="data.item.supplier" />
                </template>

                <template v-slot:cell(service)="data">
                  <span v-if="data.item.service_customer" class="instances-monitor__service instances-monitor__service--ok"
                        v-b-tooltip.hover.left="{ title: 'Servicio: ' + data.item.service_customer.name, boundary: 'window' }">
                    <AppIcon name="rocket_launch" />
                  </span>
                  <span v-else class="instances-monitor__service instances-monitor__service--missing"
                        v-b-tooltip.hover.left="{ title: 'Sin servicio asociado', boundary: 'window' }">
                    <AppIcon name="report" />
                  </span>
                </template>

                <template v-slot:cell(cost)="data">
                  <MonitorCost :value="data.item.cost" :hasService="!!data.item.service_customer" mode="badge" />
                </template>

                <template v-slot:cell(systems)="data">
                  <span class="instances-monitor__count"><AppIcon name="deployed_code" /> {{data.item.systems_count || 0}}</span>
                </template>

                <template v-slot:cell(resources)="data">
                  <MonitorResources :value="data.item.monitor_recursos" mode="badge" />
                </template>

                <template v-slot:cell(reported)="data">
                  <MonitorReportChip :item="data.item" />
                </template>

                <template v-slot:cell(avisos)="data">
                  <MonitorAvisos :value="data.item.monitor_avisos" mode="badge" />
                </template>

                <template v-slot:cell(f_action)="data">
                  <b-dropdown :right="!isMobileViewport" no-caret size="sm" variant="link" boundary="window" toggle-class="app-table-action-toggle"
                              :class="{ 'pull-right': !isMobileViewport }">
                    <template #button-content><AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" /><span v-if="isMobileViewport" class="app-table-action-label">Opciones</span></template>
                    <b-dropdown-item @click="openDrawer(data.item)"><AppIcon name="visibility" /> Ver detalle</b-dropdown-item>
                    <b-dropdown-item v-if="data.item.monitor_url" :href="data.item.monitor_url" target="_blank" rel="noopener noreferrer"><AppIcon name="open_in_new" /> Abrir en Monitor</b-dropdown-item>
                    <b-dropdown-item @click="edit(data.item)"><AppIcon name="edit" /> Editar</b-dropdown-item>
                    <b-dropdown-item v-if="!data.item.monitor_id" @click="remove(data.item)"><AppIcon name="delete" class="text-danger" /> Eliminar</b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-table>
            </template>
          </MonitorTree>
        </div>
        <EmptyState v-else icon="dns" title="No se encontraron servidores" description="Ajustá los filtros o agregá un servidor nuevo." />
        <div class="app-table-footer" v-if="!table.isBusy">
          <span class="list-view-toolbar__count">{{ visibleItems.length }} registros</span>
        </div>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <div>
            <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
              <AppIcon name="arrow_back" class="mr-1" />
              Volver
            </b-button>
          </div>
        </div>
      </b-card>

      <!-- ########################### -->
      <!-- #####     DETALLE     ##### -->
      <!-- ########################### -->
      <b-sidebar id="sidebar-instances-monitor-server"
                 v-model="drawer.active"
                 right
                 shadow
                 backdrop
                 title="Detalle del servidor"
                 width="460px">
        <div class="list-view-drawer" v-if="selected">
          <div class="list-view-drawer__header">
            <div class="list-view-drawer__title">
              <span class="list-view-drawer__code">
                <AppIcon name="dns" class="mr-1" />Servidor
              </span>
              <div>{{selected.name}}</div>
              <div class="list-view-drawer__meta">
                {{selected.subdomain || 'Sin subdominio'}}
                <span v-if="selected.monitor_id"> · Monitor: {{selected.monitor_id}}</span>
              </div>
            </div>
            <div class="list-view-drawer__nav">
              <b-button size="sm" variant="outline-secondary" :disabled="!canGoPrev" v-b-tooltip.hover title="Servidor anterior" @click="goPrev()">
                <AppIcon name="chevron-up" />
              </b-button>
              <b-button size="sm" variant="outline-secondary" :disabled="!canGoNext" v-b-tooltip.hover title="Servidor siguiente" @click="goNext()">
                <AppIcon name="chevron-down" />
              </b-button>
            </div>
          </div>

          <div class="list-view-drawer__stats">
            <div class="list-view-drawer__stat instances-monitor__stat--wide">
              <span class="list-view-drawer__stat-label">Monitor</span>
              <span class="list-view-drawer__stat-value"><MonitorReportChip :item="selected" /></span>
            </div>
            <div class="list-view-drawer__stat">
              <span class="list-view-drawer__stat-label">Instancias</span>
              <span class="list-view-drawer__stat-value"><AppIcon name="deployed_code" class="mr-1" />{{selected.systems_count || 0}}</span>
            </div>
            <div class="list-view-drawer__stat" :class="{ 'list-view-drawer__stat--warning': countAvisos(selected) }">
              <span class="list-view-drawer__stat-label">Avisos</span>
              <span class="list-view-drawer__stat-value"><AppIcon name="warning" class="mr-1" />{{countAvisos(selected)}}</span>
            </div>
          </div>

          <div class="list-view-drawer__actions">
            <b-button size="sm" variant="outline-secondary" @click="edit(selected)"><AppIcon name="edit" class="mr-1" /> Editar</b-button>
            <b-button v-if="selected.monitor_url" size="sm" variant="outline-secondary" :href="selected.monitor_url" target="_blank" rel="noopener noreferrer">
              <AppIcon name="open_in_new" class="mr-1" /> Abrir en Monitor
            </b-button>
          </div>

          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="badge" /> Datos</div>
            <div class="monitor-detail">
              <div class="monitor-detail__row">
                <span class="monitor-detail__label"><AppIcon name="folder" /> Agrupación</span>
                <span class="monitor-detail__value">
                  <span v-if="selected.categorie">{{selected.categorie.name}}</span>
                  <span v-else class="monitor-detail__empty">Sin agrupación</span>
                </span>
              </div>
              <div class="monitor-detail__row">
                <span class="monitor-detail__label"><AppIcon name="person" /> Cliente</span>
                <span class="monitor-detail__value">
                  <span v-if="selected.customer">{{selected.customer.name}}</span>
                  <span v-else class="monitor-pill monitor-pill--danger">Sin cliente</span>
                </span>
              </div>
              <div v-if="parameters.integrateServices" class="monitor-detail__row monitor-detail__row--stack">
                <span class="monitor-detail__label"><AppIcon name="rocket_launch" /> Servicio</span>
                <span class="monitor-detail__value">
                  <span v-if="selected.service_customer">{{selected.service_customer.name}}</span>
                  <span v-else class="monitor-pill monitor-pill--danger">Sin servicio</span>
                </span>
              </div>
              <div class="monitor-detail__row">
                <span class="monitor-detail__label"><AppIcon name="storefront" /> Proveedor</span>
                <span class="monitor-detail__value">
                  <span v-if="selected.supplier">{{selected.supplier.name}}</span>
                  <span v-else class="monitor-detail__empty">Sin proveedor</span>
                </span>
              </div>
              <div class="monitor-detail__row">
                <span class="monitor-detail__label"><AppIcon name="language" /> Subdominio</span>
                <span class="monitor-detail__value">
                  <span v-if="selected.subdomain" class="monitor-detail__version">{{selected.subdomain}}</span>
                  <span v-else class="monitor-detail__empty">Lo informa Monitor</span>
                </span>
              </div>
              <div v-if="selected.observations" class="monitor-detail__row monitor-detail__row--stack">
                <span class="monitor-detail__label"><AppIcon name="notes" /> Observaciones</span>
                <span class="monitor-detail__value">{{selected.observations}}</span>
              </div>
            </div>
          </div>

          <div v-if="!selected.monitor_id">
            <b-alert show variant="info" class="mb-0 py-2">
              <AppIcon name="link_off" class="mr-1" /> Pendiente de emparejar: Monitor todavía no tomó este servidor.
            </b-alert>
          </div>

          <template v-else>
            <div v-if="selected.monitor_observacion">
              <div class="list-view-drawer__section-title"><AppIcon name="sticky_note_2" /> Observaciones de Monitor</div>
              <div class="monitor-detail">
                <div class="monitor-detail__row monitor-detail__row--stack">
                  <span class="monitor-detail__value">{{selected.monitor_observacion}}</span>
                </div>
              </div>
            </div>

            <div v-if="parameters.integrateServices">
              <div class="list-view-drawer__section-title"><AppIcon name="payments" /> Costo</div>
              <MonitorCost :value="selected.cost" :hasService="!!selected.service_customer" mode="detail" />
            </div>

            <div>
              <div class="list-view-drawer__section-title"><AppIcon name="monitoring" /> Recursos</div>
              <MonitorResources :value="selected.monitor_recursos" mode="detail" />
            </div>

            <div>
              <div class="list-view-drawer__section-title"><AppIcon name="warning" /> Avisos</div>
              <MonitorAvisos :value="selected.monitor_avisos" mode="list" />
            </div>

            <div>
              <div class="list-view-drawer__section-title"><AppIcon name="terminal" /> Acceso SSH</div>
              <div v-if="sshData" class="monitor-detail">
                <div class="monitor-detail__row monitor-detail__row--stack">
                  <span class="monitor-detail__label"><AppIcon name="terminal" /> Instrucción</span>
                  <b-input-group size="sm">
                    <b-form-input :value="sshData.instruccion" readonly class="instances-monitor__ssh" />
                    <b-input-group-append>
                      <b-button variant="dark" @click="copy(sshData.instruccion)" v-b-tooltip.hover title="Copiar instrucción"><AppIcon name="content_copy" /></b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
                <div v-if="sshData.documentacion" class="monitor-detail__row monitor-detail__row--stack">
                  <span class="monitor-detail__label"><AppIcon name="menu_book" /> Cómo obtener el acceso</span>
                  <span class="monitor-detail__value">{{sshData.documentacion}}</span>
                </div>
              </div>
              <div v-else class="text-muted">Sin datos reportados</div>
            </div>
          </template>

          <div>
            <div class="list-view-drawer__section-title"><AppIcon name="deployed_code" /> Instancias en este servidor</div>
            <div v-if="drawer.loadingSystems" class="text-muted"><b-spinner small /> Cargando...</div>
            <div v-else-if="!drawer.systems.length" class="text-muted">Sin instancias asignadas</div>
            <div v-else class="monitor-detail">
              <div v-for="system in drawer.systems" :key="system.id" class="instances-monitor__system-row">
                <span class="instances-monitor__system-logo">
                  <img v-if="systemImage(system.type)" :src="systemImage(system.type)" alt="" />
                  <AppIcon v-else name="deployed_code" />
                </span>
                <span class="instances-monitor__system-text">
                  <a href="#" class="instances-monitor__link" @click.prevent="goToSystem(system)">{{system.name}}</a>
                  <small v-if="system.customer" class="text-muted">{{system.customer.name}}</small>
                </span>
                <MonitorReportChip :item="system" compact />
              </div>
            </div>
          </div>
        </div>

        <template #footer>
          <div class="list-view-drawer__footer" v-if="selected && !selected.monitor_id">
            <b-button size="sm" variant="outline-danger" block @click="remove(selected)">
              <AppIcon name="delete" class="mr-1" /> Eliminar
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <!-- ########################### -->
      <!-- #####     MODALES     ##### -->
      <!-- ########################### -->

      <!-- ALTA / EDICION (datos administrativos) -->
      <b-modal v-model="modal.form.active"
               :key="modal.form._id"
               :id="modal.form._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="dns" /></span>
          <h5 class="app-modal-header__title">{{modal.form.title}}</h5>
        </div>

        <b-alert v-if="!crud.form.id" show variant="info" class="py-2">
          <AppIcon name="info" class="mr-1" /> El servidor queda pendiente de emparejar: Monitor lo toma, lo configura e informa sus datos técnicos.
        </b-alert>

        <b-row>
          <b-col md="12">
            <b-form-group label="Nombre">
              <b-form-input type="text" size="sm" v-model="crud.form.name" required placeholder="Ingresar un nombre" />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <FindObject tag="Agrupación"
                        render="search"
                        type="instances-categories"
                        @select-object="loadCategories($event)"
                        :valueID="crud.form.categories_id"
                        :where="conditionCategoriesServer" />
          </b-col>
          <b-col md="12">
            <FindObject tag="Proveedor (opcional)"
                        render="search"
                        type="suppliers"
                        @select-object="loadSuppliers($event)"
                        :valueID="crud.form.supplier_id" />
            <small class="text-muted d-block mb-2">Donde se contrató el servidor.</small>
          </b-col>
          <b-col md="12">
            <FindObject tag="Cliente"
                        render="search"
                        type="customers"
                        @select-object="loadCustomers($event)"
                        :valueID="crud.form.customer_id"
                        :where="conditionCustomersActive" />
          </b-col>
          <b-col md="12" v-if="parameters.integrateServices && crud.form.customer_id">
            <FindObject tag="Servicio"
                        render="search"
                        display="name"
                        type="servicesCustomers"
                        @select-object="loadServicesCustomersDetails($event)"
                        :valueID="crud.form.services_customers_details_id"
                        :where="conditionServicesCustomersDetails"
                        :key="crud.form.customer_id" />
            <small class="text-muted d-block mb-2">Servicio del cliente marcado como "Vincular servidor".</small>
          </b-col>
          <b-col md="12" v-if="parameters.integrateServices">
            <b-form-group label="Costo del servidor" class="mb-2">
              <b-input-group size="sm">
                <b-input-group-prepend>
                  <b-form-select v-model="crud.form.cost_currency" :options="arr.select.currencies" size="sm" class="instances-monitor__cost-currency" />
                </b-input-group-prepend>
                <app-number-input size="sm" v-model="crud.form.cost_price" allow-empty placeholder="Sin cargar (se usa el sugerido)" />
                <b-input-group-append v-if="costSuggestion">
                  <b-button variant="outline-secondary" @click="useSuggestedCost()"
                            v-b-tooltip.hover :title="'Última renovación llevada al período: ' + costSuggestion.label">
                    <AppIcon name="lightbulb" /> Usar sugerido
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <small class="text-muted d-block mt-1">{{costHelp}}</small>
            </b-form-group>
          </b-col>
          <b-col md="12" class="mt-2">
            <b-form-group label="Observaciones">
              <app-textarea v-model="crud.form.observations" lazy placeholder="Ingresar las observaciones..." rows="3" max-rows="6" />
            </b-form-group>
          </b-col>
        </b-row>

        <div slot="modal-footer">
          <b-button variant="outline-secondary" class="mr-1" @click="modal.form.active=false">Cancelar</b-button>
          <b-button variant="dark" @click="save()"><AppIcon name="save" class="mr-1" /> Guardar</b-button>
        </div>
      </b-modal>
    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Param from '@/config/parameters'
  import Storage from '@/handler/storageLocal'
  import FindObject from '@/components/inc/find/findObject'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import { reportState, countAvisos, parseJson, resourcesCritical, RESOURCE_CRITICAL_PERCENT, serviceCostMonths, costFrequencyLabel } from './inc/monitorConfig'
  import MonitorAvisos from './inc/monitorAvisos'
  import MonitorReportChip from './inc/monitorReportChip'
  import MonitorSummary from './inc/monitorSummary'
  import MonitorAvatarName from './inc/monitorAvatarName'
  import MonitorResources from './inc/monitorResources'
  import MonitorCost from './inc/monitorCost'
  import zyncroIcon from '@/assets/branding/zyncro-icon.svg'
  import laravelIcon from '@/assets/branding/laravel-icon.svg'
  import dockerIcon from '@/assets/branding/docker-icon.svg'

  const SYSTEM_IMAGES = { zyncro: zyncroIcon, laravel: laravelIcon, docker: dockerIcon }
  import './inc/monitorDetail.css'
  import MonitorTree from './inc/monitorTree'
  import MonitorGroupBy from './inc/monitorGroupBy'
  import { buildTree, flattenTree } from './inc/monitorTreeBuilder'

  export default {
    components: {
      MonitorCost,
      MonitorAvisos,
      MonitorReportChip,
      MonitorSummary,
      MonitorAvatarName,
      MonitorResources,
      MonitorTree,
      MonitorGroupBy,
      FindObject,
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.INSTANCIAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'crudInstancesMonitorServer',
          elements: {}
        },
        table: {
          items: [],
          fields: [],
          isBusy: false,
        },
        parameters: {
          integrateServices: Helper.hasParametersAccess(Param.P59),
        },
        isMobileViewport: false,
        mobileMql: null,
        filtersOpen: false,
        expandAll: false,
        groupBy: ['categorie'],
        quickFilter: null,
        // ids que llegan desde una notificación (?ids=1,2,3)
        idsFilter: [],
        // servidor a seleccionar al llegar desde otra pantalla (params.select)
        revealId: null,
        pendingSelect: null,
        filters: {
          name: '',
          categorie: null,
          customer: null,
          supplier: null,
        },
        crud: {
          form: {
            id: 0,
            name: '',
            categories_id: 0,
            customer_id: 0,
            supplier_id: 0,
            services_customers_details_id: 0,
            observations: '',
            cost_price: '',
            cost_currency: 'ARS',
          },
          // costo: frecuencia del servicio elegido y costo del servidor que se edita
          costMonths: null,
          costItem: null,
        },
        modal: {
          form: { active: false, title: '' },
        },
        drawer: {
          active: false,
          systems: [],
          loadingSystems: false,
        },
        selected: null,
        arr: {
          select: {
            categories: [],
            customers: [],
            suppliers: [],
            currencies: ['ARS'],
          },
        },
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },
    mounted() {
      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)

      this.restoreFilterStorage()
      this.filtersOpen = Storage.getValue('filters_open_crud_instances_monitor_server') == 'true'
      this.expandAll = Storage.getValue('expand_all_crud_instances_monitor_server') == 'true'
      this.quickFilter = Storage.getValue('quick_filter_crud_instances_monitor_server') || null
      var groupBy = Storage.getValue('group_by_crud_instances_monitor_server')
      if(groupBy) {
        try {
          var keys = JSON.parse(groupBy).filter(item => this.groupOptions.some(option => option.key == item))
          if(Array.isArray(keys)) this.groupBy = keys
        } catch (error) {
          this.groupBy = ['categorie']
        }
      }

      this.loadFieldTable()
      this.filterLoadSelects()
      this.readIdsFilter()
      this.pendingSelect = this.$route.params.select || null
      this.filter()
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    watch: {
      // se puede llegar desde una notificación estando ya en la vista
      '$route.query.ids'() {
        this.readIdsFilter()
        this.filter()
      },
      // la vista queda viva en su pestaña: una nueva navegación trae otro select
      '$route'(to) {
        if(to.name == 'InstancesStaffCrudInstancesMonitorServer' && to.params.select) {
          this.pendingSelect = to.params.select
          this.selectFromRoute()
        }
      },
      filtersOpen(value) {
        Storage.setValue('filters_open_crud_instances_monitor_server', value ? 'true' : 'false')
      },
      expandAll(value) {
        Storage.setValue('expand_all_crud_instances_monitor_server', value ? 'true' : 'false')
      },
      groupBy(value) {
        Storage.setValue('group_by_crud_instances_monitor_server', JSON.stringify(value))
      },
      quickFilter(value) {
        Storage.setValue('quick_filter_crud_instances_monitor_server', value || '')
      },
    },
    computed: {
      // en mobile (tabla vertical) la columna del botón de opciones se titula "Acciones"
      tableFields() {
        return this.table.fields.map(field => field.key === 'f_action' ? Object.assign({}, field, { label: this.isMobileViewport ? 'Acciones' : '' }) : field)
      },
      // indicadores del listado (según los filtros aplicados)
      summaryTiles() {
        var items = this.table.items
        var count = fn => items.filter(fn).length
        return [
          { key: 'all', label: 'Servidores', value: items.length, icon: 'dns', tone: 'neutral' },
          { key: 'ok', label: 'Reporte al día', value: count(item => reportState(item) == 'ok'), icon: 'check_circle', tone: 'success', filter: true },
          { key: 'attention', label: 'Atrasados o sin reportes', value: count(item => ['stale', 'never'].indexOf(reportState(item)) >= 0), icon: 'schedule', tone: 'warning', filter: true },
          { key: 'avisos', label: 'Con avisos', value: count(item => countAvisos(item) > 0), icon: 'warning', tone: 'danger', filter: true },
          { key: 'resources', label: 'Recursos al ' + RESOURCE_CRITICAL_PERCENT + ' % o más', value: count(item => resourcesCritical(item)), icon: 'monitoring', tone: 'danger', filter: true },
          { key: 'pending', label: 'Pendientes de emparejar', value: count(item => reportState(item) == 'pending'), icon: 'link_off', tone: 'info', filter: true },
        ]
      },
      visibleItems() {
        // desde una notificación: solo esos registros, sin otros filtros
        if(this.idsFilter.length) {
          return this.table.items.filter(item => this.idsFilter.indexOf(item.id) >= 0)
        }
        var quick = this.quickFilter
        if(!quick) return this.table.items
        return this.table.items.filter(item => {
          var state = reportState(item)
          if(quick == 'ok') return state == 'ok'
          if(quick == 'attention') return state == 'stale' || state == 'never'
          if(quick == 'pending') return state == 'pending'
          if(quick == 'avisos') return countAvisos(item) > 0
          if(quick == 'resources') return resourcesCritical(item)
          return true
        })
      },
      // niveles disponibles del árbol
      groupOptions() {
        return [
          { key: 'categorie', label: 'Agrupación', icon: 'folder', emptyLabel: 'Sin agrupación', value: item => item.categorie },
          { key: 'customer', label: 'Cliente', icon: 'person', emptyLabel: 'Sin cliente', value: item => item.customer },
          { key: 'supplier', label: 'Proveedor', icon: 'storefront', emptyLabel: 'Sin proveedor', value: item => item.supplier },
        ]
      },
      groupLevels() {
        return this.groupBy.map(key => this.groupOptions.find(item => item.key == key)).filter(Boolean)
      },
      // árbol según el agrupamiento elegido
      tree() {
        return buildTree(this.visibleItems, this.groupLevels)
      },
      // orden del listado tal cual se ve, para navegar en el panel lateral
      orderedItems() {
        return flattenTree(this.tree, this.visibleItems)
      },
      selectedIndex() {
        return this.selected ? this.orderedItems.findIndex(item => item.id == this.selected.id) : -1
      },
      canGoPrev() {
        return this.selectedIndex > 0
      },
      canGoNext() {
        return this.selectedIndex >= 0 && this.selectedIndex < this.orderedItems.length - 1
      },
      conditionCategoriesServer() {
        return [{field: 'type', condition: 'server'}]
      },
      conditionCustomersActive() {
        return [{field: 'active', condition: true}]
      },
      // servicios del cliente del servidor marcados como "vincular servidor"
      // frecuencia de pago: la del servicio elegido o, si no se pudo leer, la que
      // informó el backend para el servicio guardado
      formCostMonths() {
        if(this.crud.costMonths) return this.crud.costMonths
        var item = this.crud.costItem
        if(item && item.cost && item.services_customers_details_id == this.crud.form.services_customers_details_id) return item.cost.months
        return null
      },
      costHelp() {
        var months = this.formCostMonths
        if(!this.crud.form.services_customers_details_id) return 'Importe que se paga por el período de renovación del servicio vinculado.'
        if(!months) return 'El servicio no tiene período de renovación: el importe se toma como mensual.'
        return 'Importe que se paga cada ' + (months == 1 ? 'mes' : months + ' meses') + ' (' + costFrequencyLabel(months).toLowerCase() + ', según el servicio). Vacío: se muestra el sugerido.'
      },
      // sugerido: última renovación del servicio guardado, llevada al período actual
      costSuggestion() {
        var item = this.crud.costItem
        var months = this.formCostMonths
        if(!item || !item.cost || !item.cost.suggested || !months) return null
        if(item.services_customers_details_id != this.crud.form.services_customers_details_id) return null
        var suggested = item.cost.suggested
        var amount = Math.round(suggested.monthly * months * 100) / 100
        return {
          amount: amount,
          currency: suggested.currency,
          label: Intl.NumberFormat('es-AR', { style: 'currency', currency: suggested.currency || 'ARS' }).format(amount),
        }
      },
      conditionServicesCustomersDetails() {
        return [
          {field: 'customer_id', condition: this.crud.form.customer_id},
          {field: 'canceled', condition: false},
          {field: 'type_instances', condition: 'server'},
          // sin los ya vinculados a otro servidor (el de este servidor sigue apareciendo)
          {field: 'available_server', condition: this.crud.form.id || 0},
        ]
      },
      // monitor_ssh: {instruccion, documentacion}
      sshData() {
        var ssh = this.selected ? parseJson(this.selected.monitor_ssh) : null
        return ssh && typeof ssh == 'object' && ssh.instruccion ? ssh : null
      },
    },
    methods: {
      countAvisos,
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      loadFieldTable() {
        this.table.fields = [
          {key: 'name', label: 'Servidor', class: 'align-middle'},
          {key: 'customer', label: 'Cliente', class: 'align-middle'},
          {key: 'supplier', label: 'Proveedor', class: 'align-middle'},
          ...(this.parameters.integrateServices ? [{key: 'service', label: 'Servicio', class: 'align-middle text-md-center'}, {key: 'cost', label: 'Costo', class: 'align-middle'}] : []),
          {key: 'systems', label: 'Instancias', class: 'align-middle text-md-center'},
          {key: 'resources', label: 'Recursos', class: 'align-middle'},
          {key: 'reported', label: 'Monitor', class: 'align-middle'},
          {key: 'avisos', label: 'Avisos', class: 'align-middle text-md-center'},
          {key: 'f_action', label: '', class: 'align-middle text-center'},
        ]
      },
      rowClass(item) {
        return item && this.selected && this.drawer.active && item.id == this.selected.id ? 'instances-monitor-table__row--selected' : 'instances-monitor-table__row'
      },
      toggleExpandAll() {
        this.expandAll = !this.expandAll
      },
      getGroupHealth(items) {
        var health = { avisos: 0, attention: 0, pending: 0 }
        items.forEach(item => {
          var state = reportState(item)
          if(countAvisos(item)) health.avisos++
          if(state == 'pending') health.pending++
          else if(state != 'ok') health.attention++
        })
        return health
      },

      // panel lateral
      openDrawer(item) {
        this.selected = item
        this.drawer.active = true
        this.loadServerSystems(item)
      },
      goPrev() {
        if(this.canGoPrev) this.openDrawer(this.orderedItems[this.selectedIndex - 1])
      },
      goNext() {
        if(this.canGoNext) this.openDrawer(this.orderedItems[this.selectedIndex + 1])
      },
      loadServerSystems(item) {
        this.drawer.systems = []
        if(!item.systems_count) return

        this.drawer.loadingSystems = true
        serviceAPI.filtrarMonitorSistema({ instances_server: item.id })
          .then((response) => {
            if(this.selected && this.selected.id == item.id) this.drawer.systems = response.data.data
            this.drawer.loadingSystems = false
          })
          .catch(() => {
            this.drawer.loadingSystems = false
          })
      },
      copy(text) {
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(text)
            .then(() => this.$awn.success('Copiado al portapapeles'))
            .catch(() => this.copyFallback(text))
          return
        }
        this.copyFallback(text)
      },
      copyFallback(text) {
        var textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        var copied = document.execCommand('copy')
        textarea.remove()
        copied ? this.$awn.success('Copiado al portapapeles') : this.$awn.alert('No se pudo copiar')
      },

      // crud
      systemImage(type) {
        return SYSTEM_IMAGES[type] || null
      },
      // abre la instancia en su vista (por params: reutiliza la pestaña de Instancias)
      goToSystem(system) {
        this.drawer.active = false
        this.$router.push({ name: 'InstancesStaffCrudInstancesMonitorSystem', params: { select: String(system.id) } })
      },
      // llegada desde Instancias (columna VPS): abre su rama y su detalle
      selectFromRoute() {
        var id = this.pendingSelect
        if(!id || this.table.isBusy || !this.table.items.length) return
        var item = this.table.items.find(server => String(server.id) == String(id))
        if(!item) {
          // los filtros guardados lo excluyen: se limpian una vez y se vuelve a buscar
          var hasFilters = this.filters.name || this.filters.categorie || this.filters.customer || this.filters.supplier
          if(hasFilters) {
            this.filters = { name: '', categorie: null, customer: null, supplier: null }
            this.filter()
          } else {
            this.pendingSelect = null
          }
          return
        }
        this.pendingSelect = null
        // si un indicador o la vista de una notificación lo ocultan, se quitan
        if(this.visibleItems.indexOf(item) < 0) {
          this.quickFilter = null
          this.idsFilter = []
        }
        this.revealId = item.id
        this.openDrawer(item)
      },
      readIdsFilter() {
        var ids = this.$route.query.ids
        this.idsFilter = ids ? String(ids).split(',').map(id => parseInt(id)).filter(id => !isNaN(id)) : []
      },
      clearIdsFilter() {
        this.idsFilter = []
        this.$router.replace({ query: {} })
      },
      filter() {
        // con ids de una notificación se trae todo, sin pisar los filtros guardados
        var params = this.idsFilter.length ? {} : this.filters
        if(!this.idsFilter.length) this.saveFilterStorage()
        this.table.isBusy = true

        var result = serviceAPI.filtrarMonitorServidor(params)

        result.then((response) => {
          this.table.items = response.data.data
          this.table.isBusy = false
          this.selectFromRoute()
          // refresca el panel abierto con los datos nuevos
          if(this.selected) {
            var fresh = this.table.items.find(item => item.id == this.selected.id)
            if(fresh) this.selected = fresh
            else this.drawer.active = false
          }
        })
        .catch(error => {
          this.table.isBusy = false
          this.$awn.alert(Error.showError(error))
        })
      },
      add() {
        this.crud.form.id = 0
        this.crud.form.name = ''
        this.crud.form.categories_id = 0
        this.crud.form.customer_id = 0
        this.crud.form.supplier_id = 0
        this.crud.form.services_customers_details_id = 0
        this.crud.form.observations = ''
        this.crud.form.cost_price = ''
        this.crud.form.cost_currency = 'ARS'
        this.crud.costMonths = null
        this.crud.costItem = null

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Nuevo Servidor"
        this.modal.form.active = true
      },
      edit(item) {
        this.crud.form.id = item.id
        this.crud.form.name = item.name
        this.crud.form.categories_id = item.categories_id
        this.crud.form.customer_id = item.customer_id || 0
        this.crud.form.supplier_id = item.supplier_id || 0
        this.crud.form.services_customers_details_id = item.services_customers_details_id || 0
        this.crud.form.observations = item.observations
        this.crud.form.cost_price = item.cost_price === null || item.cost_price === undefined ? '' : item.cost_price
        this.crud.form.cost_currency = item.cost_currency || 'ARS'
        this.crud.costMonths = null
        this.crud.costItem = item

        this.modal.form._id = Math.random().toString(36).slice(2, 8)
        this.modal.form.title = "Editar Servidor"
        this.modal.form.active = true
      },
      save() {
        let loader = this.$loading.show()
        var result = this.crud.form.id
          ? serviceAPI.editarMonitorServidor(this.crud.form)
          : serviceAPI.agregarMonitorServidor(this.crud.form)

        result.then(() => {
          this.modal.form.active = false
          loader.hide()
          this.filter()
          this.$awn.success("Cambios guardado con éxito")
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      // solo pendientes de emparejar: uno emparejado se desempareja desde Monitor
      remove(item) {
        this.confirmDialog('¿Desea eliminar el servidor (' + item.id + ') - ' + item.name + '?', {
          title: 'Eliminar Servidor',
          icon: 'delete',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if(!value) return

          let loader = this.$loading.show()
          serviceAPI.eliminarMonitorServidor(item.id)
            .then(() => {
              loader.hide()
              this.drawer.active = false
              this.filter()
              this.$awn.success("Registro eliminado")
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
        })
      },
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle,
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      loadCategories(object) {
        this.crud.form.categories_id = object ? object.id : 0
      },
      loadCustomers(object) {
        var customerId = object ? object.id : 0
        // el servicio pertenece al cliente: si cambia el cliente, se quita
        if(customerId != this.crud.form.customer_id) this.crud.form.services_customers_details_id = 0
        this.crud.form.customer_id = customerId
      },
      loadServicesCustomersDetails(object) {
        this.crud.form.services_customers_details_id = object ? object.id : 0
        this.crud.costMonths = object && object.service ? serviceCostMonths(object.service) : null
      },
      useSuggestedCost() {
        if(!this.costSuggestion) return
        this.crud.form.cost_price = this.costSuggestion.amount
        this.crud.form.cost_currency = this.costSuggestion.currency || 'ARS'
        if(this.arr.select.currencies.indexOf(this.crud.form.cost_currency) < 0) this.arr.select.currencies.push(this.crud.form.cost_currency)
      },
      loadSuppliers(object) {
        this.crud.form.supplier_id = object ? object.id : 0
      },

      // filter select
      filterLoadSelects() {
        serviceAPI.obtenerCategoriesByType('server').then((response) => {
          this.arr.select.categories = response.data.map(element => ({code: element.id, label: element.name}))
        })
        serviceAPI.obtenerClientes().then((response) => {
          this.arr.select.customers = response.data.map(element => ({code: element.id, label: (element.active ? '' : '[INACTIVO] - ') + element.name}))
        })
        serviceAPI.obtenerProveedores().then((response) => {
          this.arr.select.suppliers = response.data.map(element => ({code: element.id, label: element.name}))
        })
        // monedas para el costo (códigos sin repetir)
        if(this.parameters.integrateServices) {
          serviceAPI.obtenerMonedas().then((response) => {
            var codes = (response.data || []).map(element => element.code).filter(code => code)
            this.arr.select.currencies = codes.filter((code, index) => codes.indexOf(code) == index)
            if(!this.arr.select.currencies.length) this.arr.select.currencies = ['ARS']
          })
        }
      },

      // FILTROS SAVE
      saveFilterStorage() {
        Storage.setValue('filter_view_instances_monitor_server', JSON.stringify(this.filters))
      },
      restoreFilterStorage() {
        if(Storage.getValue('filter_view_instances_monitor_server')) {
          var saved = JSON.parse(Storage.getValue('filter_view_instances_monitor_server'))
          this.filters = Object.assign({}, this.filters, saved)
        }
        // el estado de Monitor se filtra con los indicadores; el tipo ya no existe
        delete this.filters.reporting
        delete this.filters.type
      },
    }
  }
</script>

<style scoped>
  /* mobile (tabla vertical): el botón de opciones va a la izquierda, con la palabra "Opciones" */
  @media (max-width: 767.98px) {
    .instances-monitor-table >>> .b-table-stacked-md > tbody > tr > td:last-child {
      text-align: left !important;
    }
    .instances-monitor-table >>> .app-table-action-toggle {
      width: auto;
      gap: 4px;
      padding: 0 6px !important;
      border-radius: 6px;
      font-size: 14px;
    }
    .instances-monitor-table >>> .app-table-action-label {
      display: inline;
    }
  }

  .instances-monitor__cost-currency {
    width: auto;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .instances-monitor__service {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    font-size: 16px;
    cursor: default;
  }

  .instances-monitor__service--ok {
    background: var(--app-color-success-soft);
    color: var(--app-color-success);
  }

  .instances-monitor__service--missing {
    background: var(--app-color-danger-soft);
    color: var(--app-color-danger);
    box-shadow: 0 0 0 2px var(--app-color-danger);
  }

  .instances-monitor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
  }

  .instances-monitor-table >>> .instances-monitor-table__row,
  .instances-monitor-table >>> .instances-monitor-table__row--selected {
    cursor: pointer;
  }

  .instances-monitor-table >>> .instances-monitor-table__row--selected {
    background: var(--app-color-primary-soft);
  }

  .instances-monitor__identity {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .instances-monitor__identity-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .instances-monitor__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 32px;
    height: 32px;
    border-radius: 8px;
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary-active);
    font-size: 18px;
  }

  .instances-monitor__count {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 600;
  }

  /* el estado de Monitor ocupa toda la fila: el chip ("Atrasado · hace 2 d") no
     entra en un tercio del panel */
  .instances-monitor__stat--wide {
    grid-column: 1 / -1;
  }

  .instances-monitor__system-row {
    display: grid;
    grid-template-columns: 26px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    padding: 7px 12px;
    border-bottom: 1px solid var(--app-color-border);
  }

  .instances-monitor__system-row:last-child {
    border-bottom: none;
  }

  .instances-monitor__system-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    background: var(--app-color-surface-subtle);
  }

  .instances-monitor__system-logo img {
    width: 15px;
    height: 15px;
  }

  .instances-monitor__system-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.25;
    font-size: 13px;
  }

  .instances-monitor__system-text a {
    overflow-wrap: anywhere;
  }

  .instances-monitor__link {
    color: var(--app-color-primary);
    font-weight: 600;
  }

  .instances-monitor__link:hover {
    text-decoration: underline;
  }

  .instances-monitor__ssh {
    font-family: monospace;
  }
</style>
